import axios from 'axios'

export default {
  getClient(conductorUrl) {
    return axios.create({
      baseURL: conductorUrl,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json, text/plain'
      },
      timeout: 5000
    })
  },
  // Workflow Metadata
  getWorkflowDefinitions(conductorUrl) {
    return this.getClient(conductorUrl).get('metadata/workflow')
  },
  getWorkflowDefinition(
    conductorUrl,
    workflowDefinitionName,
    workflowDefinitionVersion
  ) {
    return this.getClient(conductorUrl).get(
      'metadata/workflow/' + workflowDefinitionName,
      {
        params: {
          version: workflowDefinitionVersion
        }
      }
    )
  },
  createWorkflowDefinition(conductorUrl, workflowDefinition) {
    return this.getClient(conductorUrl).post(
      'metadata/workflow',
      workflowDefinition
    )
  },
  updateWorkflowDefinition(conductorUrl, workflowDefinition) {
    return this.getClient(conductorUrl).put('metadata/workflow', [
      workflowDefinition
    ])
  },
  removeWorkflowDefinition(
    conductorUrl,
    workflowDefinitionName,
    workflowDefinitionVersion
  ) {
    return this.getClient(conductorUrl).delete(
      'metadata/workflow/' +
        workflowDefinitionName +
        '/' +
        workflowDefinitionVersion
    )
  },
  // Task Metadata
  getTaskDefinitions(conductorUrl) {
    return this.getClient(conductorUrl).get('metadata/taskdefs')
  },
  getTaskDefinition(conductorUrl, taskType) {
    return this.getClient(conductorUrl).get('metadata/taskdefs/' + taskType)
  },
  createTaskDefinition(conductorUrl, taskDefinition) {
    return this.getClient(conductorUrl).post('metadata/taskdefs', [
      taskDefinition
    ])
  },
  updateTaskDefinition(conductorUrl, taskDefinition) {
    return this.getClient(conductorUrl).put('metadata/taskdefs', taskDefinition)
  },
  // Workflow Management
  getWorkflows(conductorUrl, params) {
    return this.getClient(conductorUrl).get('workflow/search', {
      params: params
    })
  },
  getWorkflow(conductorUrl, workflowId, includeTasks = true) {
    return this.getClient(conductorUrl).get('workflow/' + workflowId, {
      params: { includeTasks: includeTasks }
    })
  },
  startWorkflow(conductorUrl, workflow) {
    return this.getClient(conductorUrl).post('workflow', workflow)
  },
  pauseWorkflow(conductorUrl, workflowId) {
    return this.getClient(conductorUrl).put('workflow/' + workflowId + '/pause')
  },
  resumeWorkflow(conductorUrl, workflowId) {
    return this.getClient(conductorUrl).put(
      'workflow/' + workflowId + '/resume'
    )
  },
  rerunWorkflow(conductorUrl, workflowId, payload) {
    return this.getClient(conductorUrl).post(
      'workflow/' + workflowId + '/rerun',
      payload
    )
  },
  restartWorkflow(conductorUrl, workflowId, useLatestDefinitions = false) {
    return this.getClient(conductorUrl).post(
      'workflow/' + workflowId + '/restart',
      null,
      {
        params: {
          useLatestDefinitions: useLatestDefinitions
        }
      }
    )
  },
  retryWorkflow(conductorUrl, workflowId) {
    return this.getClient(conductorUrl).post(
      'workflow/' + workflowId + '/retry'
    )
  },
  skipWorkflowTask(conductorUrl, workflowId, taskReferenceName, payload) {
    return this.getClient(conductorUrl).put(
      'workflow/' + workflowId + '/skiptask/' + taskReferenceName,
      payload
    )
  },
  stopWorkflow(conductorUrl, workflowId) {
    return this.getClient(conductorUrl).delete('workflow/' + workflowId)
  },
  removeWorkflow(conductorUrl, workflowId, archiveWorkflow = true) {
    return this.getClient(conductorUrl).delete(
      'workflow/' + workflowId + '/remove',
      {
        params: { archiveWorkflow: archiveWorkflow }
      }
    )
  },
  decideWorkflow(conductorUrl, workflowId) {
    return this.getClient(conductorUrl).put('workflow/decide/' + workflowId)
  },
  // Admin
  adminConfig(conductorUrl) {
    return this.getClient(conductorUrl).get('admin/config')
  },
  adminSweepRequeue(conductorUrl, workflowId) {
    return this.getClient(conductorUrl).get('admin/sweep/requeue/' + workflowId)
  },
  adminTask(conductorUrl, taskType) {
    return this.getClient(conductorUrl).get('admin/task/' + taskType)
  },
  // Task Management
  getTasks(conductorUrl, params) {
    return this.getClient(conductorUrl).get('tasks/search', {
      params: params
    })
  },
  getTask(conductorUrl, taskId) {
    return this.getClient(conductorUrl).get('tasks/' + taskId)
  },
  updateTaskResult(conductorUrl, taskResult) {
    return this.getClient(conductorUrl).post('tasks', taskResult)
  },
  ackTask(conductorUrl, taskId, workerId = '') {
    return this.getClient(conductorUrl).post('tasks/' + taskId + '/ack', null, {
      params: { workerid: workerId }
    })
  },
  requeuePendingTasks(conductorUrl) {
    return this.getClient(conductorUrl).post('tasks/queue/requeue')
  },
  requeuePendingTasksByTaskType(conductorUrl, taskType) {
    return this.getClient(conductorUrl).post('tasks/queue/requeue/' + taskType)
  },
  dequeueTask(conductorUrl, taskType, taskId) {
    return this.getClient(conductorUrl).delete(
      'tasks/queue/' + taskType + '/' + taskId
    )
  },
  getTaskQueueSizeVerbose(conductorUrl) {
    return this.getClient(conductorUrl).get('tasks/queue/all/verbose')
  },
  getTaskQueuePollData(conductorUrl) {
    return this.getClient(conductorUrl).get('tasks/queue/polldata/all')
  },
  batchPollTask(conductorUrl, taskType, params) {
    return this.getClient(conductorUrl).get('tasks/poll/batch/' + taskType, {
      params: params
    })
  },
  // Events Management
  getEventHandlers(conductorUrl) {
    return this.getClient(conductorUrl).get('event')
  },
  getEventHandler(conductorUrl, name) {
    return this.getClient(conductorUrl).get('event/' + name)
  },
  createEventHandler(conductorUrl, eventHandler) {
    return this.getClient(conductorUrl).post('event', eventHandler)
  },
  updateEventHandler(conductorUrl, eventHandler) {
    return this.getClient(conductorUrl).put('event', eventHandler)
  },
  removeEventHandler(conductorUrl, eventHandlername) {
    return this.getClient(conductorUrl).delete('event/' + eventHandlername)
  },
  getEventHandlerQueues(conductorUrl) {
    return this.getClient(conductorUrl).get('event/queues')
  },
  getEventHandlerQueuesProviders(conductorUrl) {
    return this.getClient(conductorUrl).get('event/queues/providers')
  }
}
